import Actions from '@ppcp/actions';

/**
 * Base Gateway Interface
 *
 * Defines the minimal contract that all payment gateways must implement.
 * Each gateway handles payment-method-specific logic (PayPal, Google Pay, etc.)
 */
class BaseGateway {
    constructor({id, settings, controller}) {
        this.id = id;
        this.settings = settings;
        this.controller = controller;
        this.paypal = null;
        this.capabilities = [];
        if (this.settings.buttonPlacement === 'payment_method') {
            this.capabilities.push('place_order_button');
        }
        this.actions = new Actions(); // Shared actions instance (singleton)
    }

    initialize() {

    }

    /**
     * Set the controller instance after gateway creation
     * @param {BaseController} controller - The controller instance
     */
    setController(controller) {
        this.controller = controller;
    }

    /**
     * Set the PayPal SDK instance (shared across all gateways)
     * @param {Object} paypal - The PayPal SDK instance
     */
    setPayPal(paypal) {
        this.paypal = paypal;
    }

    /**
     * Check if the payment method can make payments
     * (Gateway-specific eligibility check before attempting to create payment elements)
     *
     * @returns {Promise<boolean>} True if the payment method is available and ready
     */
    async canMakePayment() {
        return true; // Default: assume gateway can make payments
    }

    /**
     * Check if payment elements should be created in the container
     * (Checks if elements already exist to prevent duplicates)
     *
     * @param {HTMLElement|NodeList} container - The container to check
     * @returns {boolean} True if elements should be created
     */
    shouldCreateElement(container) {
        return true;
    }

    /**
     * Create payment element(s) in the specified container
     * (Could be buttons, fields, or other payment UI elements)
     *
     * @param {HTMLElement|NodeList} container - The container element(s) to render payment UI
     * @returns {Promise<Object>} Promise that resolves with payment element instances
     */
    async createPaymentElement(container) {
        throw new Error('Must implement createPaymentElement()');
    }

    /**
     * Get the field key for storing order ID
     * @returns {string} Order field key
     */
    getOrderFieldKey() {
        return `${this.id}_paypal_order_id`;
    }

    /**
     * Get the field key for storing billing/payment token
     * @returns {string} Token field key
     */
    getTokenFieldKey() {
        return `${this.id}_payment_token`;
    }

    /**
     * Enable payment element(s)
     * Override in subclasses for gateway-specific implementation
     */
    enablePaymentElement() {
        // Override in subclasses
    }

    /**
     * Disable payment element(s)
     * Override in subclasses for gateway-specific implementation
     */
    disablePaymentElement() {
        // Override in subclasses
    }

    /**
     * Destroy payment element(s) and clean up resources
     * Override in subclasses for gateway-specific implementation
     */
    destroyPaymentElement() {
        // Override in subclasses
    }

    /**
     * Validate payment element before form submission
     * Override in subclasses for gateway-specific validation (e.g., credit card fields)
     * @returns {boolean} True if valid, false if invalid
     * @throws {Error} If validation fails with specific error message
     */
    validatePaymentElement() {
        return true; // Default: no validation needed (PayPal, Google Pay handle their own validation)
    }

    /**
     * Check if gateway supports a specific capability
     * @param {string} capability - The capability to check (e.g., 'place_order_button', 'tokenization')
     * @returns {boolean} True if gateway supports the capability
     */
    supports(capability) {
        return this.capabilities.includes(capability);
    }
}

export {BaseGateway};
export default BaseGateway;
